<template>
  <b-row class="map-grid-container">
    <b-col cols="auto" class="pr-0 overflow-auto h-100">
      <!-- LEFT SIDE -->
      <!-- BUTTONS -->
      <b-navbar sticky class="bg-white border-bottom">
        <b-navbar-nav small class="ml-auto">
          <slot name="buttons-left"></slot>
          <b-nav-item @click="showHideAll" :disabled="!tracksForList.length">
            <template v-if="tracksForList.length && isAllShown">Hide all</template>
            <template v-else>Show all</template>
          </b-nav-item>
        </b-navbar-nav>
      </b-navbar>
      <!-- End BUTTONS -->
  
      <!-- LIST -->
      <template v-if="tracksForList.length">
        <b-card no-body v-for="day in tracksForList" v-bind:key="day.title" class="border-0">
          <b-card-header class="bg-light rounded-0 p-1">{{day.title}}</b-card-header>
          <b-list-group flush>
            <b-list-group-item v-for="track in day.tracks" v-bind:key="track._id" class="p-1">
              <b-row class="m-0 d-flex h-100">
                <b-col class="p-0">
                  <b-row class="m-0">
                    <b-col cols="auto" class="p-0">
                      <span class="h4 font-weight-normal">{{new Date(track.startDate).toLocaleTimeString()}} - {{new Date(track.endDate).toLocaleTimeString()}}</span>
                    </b-col>
                    <b-col class="pr-1 text-right">
                      <span class="h5 font-weight-normal">{{ track.distanceInKm }} km</span>
                    </b-col>
                  </b-row>
                  <b-row>
                    <b-col>
                      <span class="h6 text-muted">{{track.motorbike && track.motorbike._name || '-'}}</span>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col cols="auto" class="p-1 justify-content-center align-self-center">
                  <base-button size="sm" type="neutral" @click="toggleVisibility(track._id)">
                    <b-icon-eye-fill v-if="isTrackVisible(track._id)"></b-icon-eye-fill>
                    <b-icon-eye-slash v-else></b-icon-eye-slash>
                  </base-button>
                </b-col>
              </b-row>
            </b-list-group-item>
          </b-list-group>
        </b-card>
      </template>
      <template v-else>
        <p class="text-center mx-auto mt-6 no-data">
          There are no tracks for these search criteria.
        </p>
      </template>
      <!-- End LIST -->
      <!-- End LEFT SIDE -->
    </b-col>
    <b-col class="pl-0 h-100">
      <!-- MAP -->
      <l-map
        ref="myMap"
        :zoom="zoom"
        :center="center"
      >
        <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
        <l-polyline v-for="(track, i) in visibleTracks" :lat-lngs="track.polyline.latlngs" :color="track.polyline.color" v-bind:key="i">
          <l-popup>
            <dl>
              <dt>Start Date</dt><dd>{{new Date(track.startDate)}}</dd>
              <dt>End Date</dt><dd>{{new Date(track.endDate)}}</dd>
              <dt>Time</dt><dd>{{((new Date(track.endDate).getTime() - new Date(track.startDate).getTime()) / 1000 / 60).toFixed()}} minutes</dd>
              <dt>Distance</dt><dd>{{ track.distanceInKm }} km</dd>
              <dt>Average speed</dt><dd>{{((track.distance / 1000) / ((new Date(track.endDate).getTime() - new Date(track.startDate).getTime()) / 1000 / 60 / 60)).toFixed()}} km/h</dd>
              <dt>Motorbike</dt><dd>{{track.motorbike && track.motorbike._name || '-'}}</dd>
            </dl>
          </l-popup>
        </l-polyline>
        <template v-for="(track, i) in visibleTracks">
          <l-marker :lat-lng="track.polyline.latlngs[0]" v-bind:key="`start-${i}`">
            <l-icon icon-url="/img/icons/marker_start.png" shadow-url="/img/icons/marker_start.png" :shadow-size="[0, 0]"></l-icon>
          </l-marker>
          <l-marker :lat-lng="track.polyline.latlngs[track.polyline.latlngs.length -1]" v-bind:key="`finish-${i}`">
            <l-icon icon-url="/img/icons/marker_finish.png" shadow-url="/img/icons/marker_finish.png" :shadow-size="[0, 0]"></l-icon>
          </l-marker>
        </template>
      </l-map>
      <!-- End MAP -->
    </b-col>
  </b-row>
</template>
<script>
  import { mapActions, mapGetters } from 'vuex';
  
  export default {
    name: "toosho-map",
    emits: [
      'shownTracksChanged',
      'allTracksChanged'
    ],
    props: {
      fromDate: {
        type: Date,
        description: 'Start date to filter',
        default: new Date().setUTCHours(0, 0, 0, 0)
      },
      toDate: {
        type: Date,
        description: 'End date to filter',
        default: new Date().setUTCHours(23, 59, 59, 999)
      },
      motorbike: {
        type: String,
        description: 'Motorbike id; null for any',
        default: null
      },
      idsToShow: {
        type: Array,
        description: 'Ids of the tracks to show',
        default: () => []
      },
      keepIdsToShow: {
        type: Boolean,
        description: 'True for keep tracks visibles when change fromDate, toDate or motorbike',
        default: false
      }
    },
    data () {
      return {
        url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution:
          '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        zoom: 10,
        center: [0, 0],
        bounds: null,
        tracksToShowIds: []
      };
    },
    methods: {
      ...mapActions({
        fetchTracks: 'tracks/fetch'
      }),
      async updateTracks () {
        await this.fetchTracks(this.filters)
      },
      toggleVisibility(trackId) {
        if (this.tracksToShowIds.find(vtrackId => vtrackId === trackId)) {
          this.tracksToShowIds = this.tracksToShowIds.filter(vtrackId => vtrackId !== trackId)
        } else {
          this.tracksToShowIds.push(trackId)
        }
        this.updateCenter()
      },
      isTrackVisible(trackId) {
        return !!(this.tracksToShowIds.find(vtrackId => vtrackId === trackId))
      },
      showHideAll() {
        if (this.isAllShown) {
          this.tracksToShowIds = []
        } else {
          this.tracksToShowIds = this.tracks.map(track => track._id)
        }
        this.updateCenter()
      },
      updateCenter() {
        const bounds = L.polygon(this.visibleTracks.flatMap(track => track.polyline.latlngs)).getBounds()
        if (bounds.isValid()) {
          this.$refs.myMap.mapObject.fitBounds(bounds)
        } else {
          this.center = this.calculatedCenter
        }
      }
    },
    async mounted() {
      this.tracksToShowIds = this.idsToShow
      await this.updateTracks()
      this.updateCenter()
    },
    computed: {
      ...mapGetters({
        tracks: 'tracks/items'
      }),
      filters() {
        return {
          fromDate: this.fromDate,
          toDate: this.toDate,
          motorbike: this.motorbike
        }
      },
      tracksForList() {
        const days = [...new Set(this.tracks.map(track => new Date(track.startDate).toInputDateFormat()))]
        return days.map(day => {
          return {
            title: day.fromInputDateFormatToDate().toLocaleDateString(),
            tracks: this.tracks.filter(track => new Date(track.startDate).toInputDateFormat() === day)
          }
        })
      },
      visibleTracks() {
        return this.tracks.filter(track => this.tracksToShowIds.find(vtrackId => vtrackId === track._id))
      },
      isAllShown(){
        return this.tracksToShowIds.length === this.tracks.length
      },
      calculatedCenter() {
        // Start point of last track (even hidden)
        const defaultCenter = this.tracks.length && this.tracks[this.tracks.length - 1] && this.tracks[this.tracks.length - 1].polyline.latlngs[0]
        // Without any track
        const noCenter = [0, 0]
        // Calc start point of last visible track
        const lastTrack = this.visibleTracks.length && this.visibleTracks[this.visibleTracks.length -1]
        const lastTrackCenter = lastTrack && lastTrack.polyline.latlngs[0]
        // Return
        return lastTrackCenter || defaultCenter || noCenter
      }
    },
    watch: {
      fromDate: function (newValue, oldValue) {
        if (newValue.getTime() !== oldValue.getTime()) {
          if (!this.keepIdsToShow) {
            this.tracksToShowIds = []
          }
          this.updateTracks()
        }
      },
      toDate: function (newValue, oldValue) {
        if (newValue.getTime() !== oldValue.getTime()) {
          if (!this.keepIdsToShow) {
            this.tracksToShowIds = []
          }
          this.updateTracks()
        }
      },
      motorbike: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          if (!this.keepIdsToShow) {
            this.tracksToShowIds = []
          }
          this.updateTracks()
        }
      },
      tracksToShowIds: function (newValue) {
        this.$emit('shownTracksChanged', newValue)
      },
      tracksForList: function (newValue) {
        this.$emit('allTracksChanged', newValue)
      }
    }
  };
</script>
<style scoped>
  .dropdown {
    z-index: 1000;
  }
  .map-grid-container {
    height: 500px;
  }
  .no-data {
    max-width: 200px;
  }
</style>
